import './polyfills';
import React, { lazy } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import SearchResultTabsContainer from './Containers/SearchResultTabs.container';
import PopularFilters from './Components/PopularFilters';
import DynamicComponent from './Components/DynamicComponent';
import { ThemeProvider } from '@material-ui/core/styles';
import KransensGummiMuiTheme from './KransensGummiMuiTheme';
import colors from 'Exports/colors.scss';
import Spinner from './Components/Spinner';
import CheckoutLoading from './Components/Checkout.Loading';
// Load vanilla components
import './Containers/Vanilla.container';
import CopyButton from './Components/CopyButton';
import HoverTooltip from './Components/HoverTooltip';
import NavigationContainer from './Containers/Navigation.container';
import { ReactNotifications } from 'react-notifications-component';
import 'react-notifications-component/dist/scss/notification.scss';
import VanillaEvents from './Components/VanillaEvents';
import { showLogin } from './Actions/Login.action';
import './Redux.server';

ReduxServer.createStore(global.LitiumPreloadState);

const hydrateServerComponents = () => {
  import('./expose-components').then(() => {
    const reactLoadedEvent = new Event('ReactLoaded');
    window.dispatchEvent(reactLoadedEvent);
  });
};

hydrateServerComponents();

window.__litium = window.__litium || {};

// Add a dictionary to keep track of the registered async reducers
global.LitiumReduxStore.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
global.LitiumReduxStore.injectReducer = (key, asyncReducer) => {
  if (!global.LitiumReduxStore.asyncReducers[key]) {
    global.LitiumReduxStore.asyncReducers[key] = asyncReducer;
    global.LitiumReduxStore.replaceReducer(
      createReducer(global.LitiumReduxStore.asyncReducers)
    );
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(
    <ThemeProvider theme={KransensGummiMuiTheme}>{element}</ThemeProvider>,
    container,
    callback
  );
};

window.addEventListener('reloadButton', function (e) {
  var buttonShadow = document.getElementById(
    'rerendered-buybutton-' + e.detail
  );
  var dataset = buttonShadow.previousElementSibling?.dataset;

  const { articleNumber, quantityFieldId, href, cssClass } = dataset;

  const BuyButton = DynamicComponent({
    loader: () => import('./Components/BuyButton'),
  });

  renderReact(
    <Provider store={global.LitiumReduxStore}>
      <BuyButton
        {...{
          articleNumber,
          quantityFieldId,
          href,
          cssClass,
        }}
      />
    </Provider>,
    buttonShadow
  );
});

const bootstrapComponents = () => {
  if (document.getElementById('quickSearch')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <QuickSearch />
      </Provider>,
      document.getElementById('quickSearch')
    );
  }
  if (document.getElementById('navbar')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NavigationContainer />
      </Provider>,
      document.getElementById('navbar')
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('search-popular-filters')) {
    const PopularFilters = DynamicComponent({
      loader: () => import('./Components/PopularFilters'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <PopularFilters />
      </Provider>,
      document.getElementById('search-popular-filters')
    );
  }

  const searchTabsRoot = document.getElementById('search-result-content-tabs');
  if (searchTabsRoot) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <SearchResultTabsContainer
          showCategoryTab={searchTabsRoot.dataset.showCategoryTab}
          showPagesTab={searchTabsRoot.dataset.showPagesTab}
        />
      </Provider>,
      searchTabsRoot
    );
  }

  const advancedSearchComponentElem = document.getElementById(
    'advanced-search-component'
  );
  if (
    advancedSearchComponentElem &&
    !registeredContainers.includes(advancedSearchComponentElem)
  ) {
    const AdvancedSearch = DynamicComponent({
      loader: () => import('./Components/AdvancedSearch/AdvancedSearch'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <AdvancedSearch />
      </Provider>,
      advancedSearchComponentElem
    );
  }

  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesPersonListContainer'),
    });
    import('./Reducers/MyPage.reducer').then(({ myPage }) => {
      global.LitiumReduxStore.injectReducer('myPage', myPage);
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <PersonList />
        </Provider>,
        document.getElementById('myPagePersons')
      );
    });
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
      loading: <CheckoutLoading />,
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }

  const filterBarElem = document.getElementById('filterbar');
  if (filterBarElem) {
    const FilterBar = DynamicComponent({
      loader: () => import('./Components/FilterBar'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FilterBar
          isSearchResultPage={
            filterBarElem.dataset['isSearchResultPage'] == 'True'
          }
          isWheelBuilderPage={
            filterBarElem.dataset['isWheelbuilderPage'] == 'True'
          }
        />
      </Provider>,
      filterBarElem
    );
  }

  if (document.getElementById('activeFiltersContainer')) {
    const ActiveFilters = DynamicComponent({
      loader: () => import('./Components/ActiveFilters'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ActiveFilters />
      </Provider>,
      document.getElementById('activeFiltersContainer')
    );
  }

  const productListPaginationElem = document.getElementById(
    'productlistPagination'
  );
  if (productListPaginationElem) {
    const ProductPaginationContainer = DynamicComponent({
      loader: () => import('./Containers/ProductPagination.container'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductPaginationContainer
          pageSize={productListPaginationElem.dataset['pageSize']}
        />
      </Provider>,
      productListPaginationElem
    );
  }

  if (document.getElementById('my-pages-navigation')) {
    const MyPagesNavigation = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesNavigation'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MyPagesNavigation />
      </Provider>,
      document.getElementById('my-pages-navigation')
    );
  }
  const customerApplicationsRoot = document.getElementById('customer-applications-app');
  if (customerApplicationsRoot) {
    import(
      /* webpackChunkName: "customer-applications" */
      './Features/CustomerApplications'
    ).then(({ mountDashboard }) => {
      mountDashboard({ container: customerApplicationsRoot });
    });
  }
  const customerRegistrationFormRoot = document.getElementById('customer-registration-form');
  if (customerRegistrationFormRoot) {
    import(
      /* webpackChunkName: "customer-applications" */
      './Features/CustomerApplications'
    ).then(({ mountRegistrationForm }) => {
      mountRegistrationForm({ container: customerRegistrationFormRoot });
    });
  }
  if (document.getElementById('my-pages-tab-content')) {
    const MyPagesTabContainer = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesTabContainer'),
    });

    import('./Reducers/Invoices.reducer').then(({ invoices }) => {
      global.LitiumReduxStore.injectReducer('invoices', invoices);

      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <MyPagesTabContainer />
        </Provider>,
        document.getElementById('my-pages-tab-content')
      );
    });
  }

  let fixedButtons = document.getElementsByClassName('fixed-button');
  if (fixedButtons != null && fixedButtons.length > 0) {
    const FixedButtonContainer = DynamicComponent({
      loader: () => import('./Containers/FixedButton.container'),
    });
    Array.from(fixedButtons).forEach((btn) => {
      const { color, textcolor, type } = btn.dataset;
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <FixedButtonContainer
            color={color}
            textcolor={textcolor}
            type={type}
          />
        </Provider>,
        btn
      );
    });
  }

  let productPriceModelElems = document.getElementsByClassName(
    'product-price-model'
  );
  if (productPriceModelElems?.length > 0) {
    const ProductPriceModel = DynamicComponent({
      loader: () => import('./Components/ProductPriceModel'),
    });
    for (let elem of productPriceModelElems) {
      const props = JSON.parse(elem.querySelector('json-data').innerText);
      const showPriceToggle = elem.dataset['showPriceToggle'] == 'true';
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <ProductPriceModel
            {...props}
            showPriceToggle={showPriceToggle}
            onQuantityToggle={() => location.reload()}
          />
        </Provider>,
        elem
      );
    }
  }

  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    const rootElement = document.getElementById('lightBoxImages');
    const images = Array.from(rootElement.querySelectorAll('a')).map((img) => ({
      src: img.dataset.src,
    }));
    const thumbnails = Array.from(
      rootElement.querySelectorAll('a img')
    ).map((img) => ({ src: img.src }));
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <LightboxImages images={images} thumbnails={thumbnails} />
      </Provider>,
      document.getElementById('lightBoxImages')
    );
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('.slider__block')].map(
          (block) => {
            return {
              image: block.dataset.image,
              url: block.dataset.url,
              text: block.dataset.text,
              actionText: block.dataset.actionText,
            };
          }
        );
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
        userIsAuthenticated,
      } = button.dataset;
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
              userIsAuthenticated: userIsAuthenticated == 'True',
            }}
          />
        </Provider>,
        button
      );
    });
  }

  if (document.querySelectorAll('reorder-button').length > 0) {
    const ReorderButton = DynamicComponent({
      loader: () => import('./Components/ReorderButton'),
    });
    Array.from(document.querySelectorAll('reorder-button')).forEach(
      (button) => {
        const { cssClass, orderId, title } = button.dataset;
        const label = button.innerText;
        renderReact(
          <Provider store={global.LitiumReduxStore}>
            <ReorderButton {...{ label, title, cssClass, orderId }} />
          </Provider>,
          button
        );
      }
    );
  }

  let overlayContainerElem = document.getElementById('overlayContainer');
  if (overlayContainerElem) {
    const OverlayContainer = DynamicComponent({
      loader: () => import('./Containers/Overlay.container'),
    });

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <OverlayContainer />
      </Provider>,
      overlayContainerElem
    );
  }

  var copyBtnElems = document.getElementsByClassName('copy-button');
  if (copyBtnElems != null && copyBtnElems.length > 0) {
    Array.from(copyBtnElems).forEach((btn) => {
      renderReact(<CopyButton text={btn.dataset.copy} />, btn);
    });
  }

  var hoverElems = document.getElementsByClassName('hover-tooltip');
  if (hoverElems != null && hoverElems.length > 0) {
    Array.from(hoverElems).forEach((item) => {
      renderReact(
        <HoverTooltip
          title={item.dataset.tooltipText}
          domElement={item.children[0]}
        />,
        item
      );
    });
  }

  const loginButtonElem = document.getElementById(
    'login-menu-trigger-container'
  );
  if (loginButtonElem) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <VanillaEvents
          selector="#login-menu-trigger"
          events={[
            {
              name: 'click',
              func: (e) => {
                e.preventDefault();
                global.LitiumReduxStore.dispatch(showLogin(true));
              },
            },
          ]}
        />
      </Provider>,
      loginButtonElem
    );
  }

  const myPageButtonElem = document.getElementById(
    'my-page-menu-trigger-container'
  );
  if (myPageButtonElem) {
    const MyPagesDropdown = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesDropdown'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MyPagesDropdown
          selector="#login-menu-trigger"
          myPagesUrl={myPageButtonElem.dataset['myPagesUrl']}
        />
      </Provider>,
      myPageButtonElem
    );
  }

  const loginContainer = document.getElementById('login-container');
  if (loginContainer) {
    const LoginModal = DynamicComponent({
      loader: () => import('./Components/LoginModal/LoginModal'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <LoginModal />
      </Provider>,
      loginContainer
    );
  }

  const productImageSliderElem = document.getElementById(
    'product-image-slider'
  );
  if (productImageSliderElem) {
    const ProductImageSlider = DynamicComponent({
      loader: () => import('./Components/ProductImageSlider'),
    });
    const images = Array.from(
      productImageSliderElem.querySelectorAll('img')
    ).map((img) => ({
      src: img.src,
      alt: img.alt,
      hdSrc: img.dataset.hdImage,
      thumbnailSrc: img.dataset.thumbnailImage,
    }));

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductImageSlider
          images={images}
          minHeight={productImageSliderElem.dataset.minHeight}
        />
      </Provider>,
      productImageSliderElem
    );
  }

  const mainProductPageTabElem = document.getElementById(
    'product-page-tab-main'
  );
  if (mainProductPageTabElem) {
    const ProductPageTabMain = DynamicComponent({
      loader: () => import('./Components/ProductPageTabs/ProductPageTab.Main'),
      loading: <Spinner />,
    });
    const options = JSON.parse(
      mainProductPageTabElem.querySelector('json-data').innerText
    );
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductPageTabMain
          options={options}
          {...mainProductPageTabElem.dataset}
          userIsAuthenticated={
            mainProductPageTabElem.dataset['userIsAuthenticated'] === 'True'
          }
        />
      </Provider>,
      mainProductPageTabElem
    );
  }

  const productPageTabRegNoElem = document.getElementById(
    'product-page-tab-regno'
  );
  if (productPageTabRegNoElem) {
    const ProductPageTabRegNo = DynamicComponent({
      loader: () => import('./Components/ProductPageTabs/ProductPageTab.RegNo'),
      loading: <Spinner />,
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductPageTabRegNo {...productPageTabRegNoElem.dataset} />
      </Provider>,
      productPageTabRegNoElem
    );
  }

  const productPageTabModelElem = document.getElementById(
    'product-page-tab-model'
  );
  if (productPageTabModelElem) {
    const ProductPageTabCarModel = DynamicComponent({
      loader: () =>
        import('./Components/ProductPageTabs/ProductPageTab.CarModel'),
      loading: <Spinner />,
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductPageTabCarModel {...productPageTabRegNoElem.dataset} />
      </Provider>,
      productPageTabModelElem
    );
  }

  var quickOrderContainer = document.getElementById('quick-order-container');
  if (quickOrderContainer) {
    const QuickOrderContainer = DynamicComponent({
      loader: () => import('./Containers/QuickOrder.container'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <QuickOrderContainer />
      </Provider>,
      quickOrderContainer
    );
  }

  const orderHistoryListElem = document.getElementById('orderHistoryPage');
  if (orderHistoryListElem) {
    const OrderHistoryListContainer = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
      loading: <Spinner />,
    });
    import('./Reducers/MyPage.reducer').then(({ myPage }) => {
      global.LitiumReduxglobal.LitiumReduxStore.injectReducer('myPage', myPage);
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <OrderHistoryListContainer />
        </Provider>,
        orderHistoryListElem
      );
    });
  }

  const wheelBuilderElem = document.getElementById('wheel-builder');
  if (wheelBuilderElem) {
    const WheelBuilderContainer = DynamicComponent({
      loader: () => import('./Components/WheelBuilder/WheelBuilder.Container'),
      loading: false,
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <WheelBuilderContainer />
      </Provider>,
      wheelBuilderElem
    );
  }

  const carPreviewElem = document.getElementById('car-preview');
  if (carPreviewElem) {
    const CarPreview = DynamicComponent({
      loader: () => import('./Components/CarPreview'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <CarPreview />
      </Provider>,
      carPreviewElem
    );
  }

  const navigationMobileTrigger = document.getElementById(
    'navigation-mobile-trigger'
  );
  if (navigationMobileTrigger) {
    const NavigationMobileTrigger = DynamicComponent({
      loader: () => import('./Components/NavigationMobileTrigger'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NavigationMobileTrigger element={navigationMobileTrigger} />
      </Provider>,
      navigationMobileTrigger
    );
  }

  const notificationsContainerElem = document.getElementById(
    'notifications-container'
  );
  if (notificationsContainerElem) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ReactNotifications />
      </Provider>,
      notificationsContainerElem
    );
  }

  const priceToggleElems = document.getElementsByClassName('price-toggle');
  if (priceToggleElems?.length > 0) {
    const PriceToggle = DynamicComponent({
      loader: () => import('./Components/PriceToggle'),
    });
    for (let elem of priceToggleElems) {
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <PriceToggle />
        </Provider>,
        elem
      );
    }
  }

  const registerUserForm = document.getElementById('register-user-form');
  if (registerUserForm) {
    const RegisterUserForm = DynamicComponent({
      loader: () => import('./Components/RegisterUserForm'),
    });
    const uspsJson = document.getElementById('register-user-form').dataset.usps;
    const usps = JSON.parse(decodeURIComponent(uspsJson));
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <RegisterUserForm usps={usps} />
      </Provider>,
      registerUserForm
    );
  }

  const offerMenuTriggerElem = document.getElementById(
    'offer-menu-trigger-container'
  );
  if (offerMenuTriggerElem) {
    const OfferTriggerButton = DynamicComponent({
      loader: () => import('./Components/Offers/Offers.TriggerButton'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <OfferTriggerButton />
      </Provider>,
      offerMenuTriggerElem
    );
  }

  const helpTriggerElem = document.getElementById('help-trigger-container');
  if (helpTriggerElem) {
    const HelpTriggerButton = DynamicComponent({
      loader: () => import('./Components/ContactUs/ContactUs.Triggerbutton'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <HelpTriggerButton />
      </Provider>,
      helpTriggerElem
    );
  }

  const productPageTabsContainerElem = document.getElementById(
    'product-page-tabs-container'
  );
  if (productPageTabsContainerElem) {
    const ProductPageTabsContainer = DynamicComponent({
      loader: () =>
        import('./Components/ProductPageTabs/ProductPageTabs.Container'),
    });
    const props = JSON.parse(
      productPageTabsContainerElem.querySelector('json-data').innerText
    );
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductPageTabsContainer {...props} />
      </Provider>,
      productPageTabsContainerElem
    );
  }

  // Lazy load animate.css stylesheet
  document.addEventListener('DOMContentLoaded', () => {
    const animateCssLink = document.createElement('link');
    animateCssLink.rel = 'stylesheet';
    animateCssLink.href =
      'https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css';
    document.querySelector('head').appendChild(animateCssLink);
  });
};

bootstrapComponents();

function getParameterCaseInsensitive(object, key) {
  return object[
    Object.keys(object).filter(function (k) {
      return k.toLowerCase() === key.toLowerCase();
    })[0]
  ];
}
